import { useGSAP } from '@gsap/react'
import { createFileRoute, Link } from '@tanstack/react-router'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useRef } from 'react'

import '../styles/landing-redesign.css'

export const Route = createFileRoute('/')({
  component: HomePage,
})

function HomePage() {
  const rootRef = useRef<HTMLDivElement>(null)

  useGSAP(
    () => {
      gsap.registerPlugin(ScrollTrigger)
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (reduce) return

      gsap.from('.ootopia-elite-hero-copy > *', {
        y: 18,
        opacity: 0,
        duration: 0.7,
        stagger: 0.08,
        ease: 'power3.out',
        delay: 0.15,
      })

      gsap.to('.ootopia-elite-hero-device .ootopia-iphone-device', {
        yPercent: -4,
        scrollTrigger: {
          trigger: '.ootopia-elite-hero',
          start: 'top top',
          end: 'bottom top',
          scrub: 1.2,
        },
      })

      gsap.from('.ootopia-bento-card', {
        y: 28,
        opacity: 0,
        duration: 0.8,
        stagger: 0.07,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.ootopia-bento',
          start: 'top 82%',
        },
      })

      const pinLeft = document.querySelector('.ootopia-pin-left')
      const pinWrap = document.querySelector('.ootopia-pin-wrap')
      if (pinLeft && pinWrap && window.innerWidth >= 1024) {
        ScrollTrigger.create({
          trigger: pinWrap,
          pin: pinLeft,
          start: 'top 14%',
          end: 'bottom bottom',
          pinSpacing: false,
        })
      }

      gsap.utils.toArray<HTMLElement>('.ootopia-scale-img').forEach((el) => {
        gsap.fromTo(
          el,
          { scale: 0.94 },
          {
            scale: 1,
            ease: 'none',
            scrollTrigger: {
              trigger: el,
              start: 'top 88%',
              end: 'top 28%',
              scrub: 1,
            },
          },
        )
      })

      gsap.utils.toArray<HTMLElement>('.ootopia-reveal-line').forEach((el) => {
        gsap.from(el, {
          yPercent: 18,
          opacity: 0,
          duration: 0.6,
          ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 88%' },
        })
      })

      gsap.from('.ootopia-stack-card', {
        y: 40,
        opacity: 0,
        stagger: 0.12,
        scrollTrigger: { trigger: '.ootopia-stack', start: 'top 75%' },
      })
    },
    { scope: rootRef },
  )

  return (
    <div ref={rootRef} className="ootopia-landing-elite overflow-x-hidden w-full max-w-full">
      <header className="ootopia-elite-nav">
        <div className="ootopia-elite-nav-inner">
          <Link to="/" className="ootopia-elite-brand" aria-label="Ootopia home">
            <img
              src="/brand/ootopia-mark-alpha.png"
              alt=""
              className="ootopia-elite-brand-mark"
              aria-hidden="true"
            />
            <img
              src="/brand/ootopia-wordmark-alpha.png"
              alt="ootopia"
              className="ootopia-elite-brand-wordmark"
            />
          </Link>
          <nav aria-label="Primary">
            <ul className="ootopia-elite-nav-links">
              <li>
                <Link to="/looks">Looks</Link>
              </li>
              <li>
                <Link to="/kit">My Kit</Link>
              </li>
              <li>
                <Link to="/import">Create</Link>
              </li>
              <li>
                <Link to="/settings/privacy">Profile</Link>
              </li>
            </ul>
          </nav>
          <Link to="/import" className="ootopia-elite-nav-cta">
            Start a look
          </Link>
        </div>
      </header>

      <main className="w-full">
        {/* Hero */}
        <section className="ootopia-elite-hero" aria-labelledby="landing-hero-title">
          <div className="ootopia-elite-hero-glow" aria-hidden="true" />
          <div className="ootopia-elite-hero-grid">
            <div className="ootopia-elite-hero-copy">
              <p className="ootopia-elite-eyebrow">Beauty discovery, made personal</p>
              <h1 id="landing-hero-title" className="ootopia-elite-h1">
                Any look <span className="ootopia-elite-h1-pill" aria-hidden="true" /> made{' '}
                <em>yours.</em>
              </h1>
              <p className="ootopia-elite-lede">
                See a look you love? Paste the link. Ootopia reads the makeup, understands your
                skin, and rebuilds it with what's already in your kit — plus the one swap actually
                worth making.
              </p>
              <div className="ootopia-elite-actions">
                <Link to="/import" className="ootopia-elite-btn ootopia-elite-btn--dark">
                  Try a creator look
                  <span aria-hidden="true" className="ootopia-elite-btn-arrow">
                    →
                  </span>
                </Link>
                <Link
                  to="/capture"
                  search={{ planId: undefined, returnTo: undefined }}
                  className="ootopia-elite-btn ootopia-elite-btn--ghost"
                >
                  Scan my skin
                </Link>
              </div>
              <p className="ootopia-elite-micro">
                No scores. No diagnoses. Just your tone, texture, and comfort — quietly guiding
                every recommendation.
              </p>
            </div>

            <figure
              className="ootopia-elite-hero-device ootopia-landing-split__hero-device"
              aria-label="iPhone skin scan preview"
            >
              <div className="ootopia-iphone-device">
                <div className="ootopia-iphone-screen">
                  <div className="ootopia-iphone-island" aria-hidden="true" />
                  <div className="ootopia-iphone-topbar">
                    <span>Skin scan</span>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 21c1.5-3.5 4.5-5 8-5s6.5 1.5 8 5" />
                    </svg>
                  </div>
                  <div className="ootopia-iphone-face">
                    <img
                      src="/homepage/synthetic-beauty-portrait.png"
                      alt="Synthetic beauty portrait inside the Ootopia skin scan"
                      decoding="async"
                    />
                    <span className="scan-corner scan-corner-tl" aria-hidden="true" />
                    <span className="scan-corner scan-corner-tr" aria-hidden="true" />
                    <span className="scan-corner scan-corner-bl" aria-hidden="true" />
                    <span className="scan-corner scan-corner-br" aria-hidden="true" />
                    <span className="scan-grid" aria-hidden="true" />
                    <span className="ootopia-iphone-scan" aria-hidden="true" />
                  </div>
                  <div className="ootopia-iphone-signals">
                    <span>Tone</span>
                    <span>Undertone</span>
                    <span>Texture</span>
                  </div>
                </div>
              </div>
              <figcaption>One clear frame in natural light. Consent first, always.</figcaption>
            </figure>
          </div>
        </section>

        <div className="ootopia-marquee" aria-hidden="true">
          <div className="ootopia-marquee-track">
            <span>Creator looks</span>
            <span className="ootopia-marquee-dot" />
            <span>Skin context</span>
            <span className="ootopia-marquee-dot" />
            <span>Your kit</span>
            <span className="ootopia-marquee-dot" />
            <span>An honest recipe</span>
            <span className="ootopia-marquee-dot" />
            <span>Try-on preview</span>
            <span className="ootopia-marquee-dot" />
            <span>Creator looks</span>
            <span className="ootopia-marquee-dot" />
            <span>Skin context</span>
            <span className="ootopia-marquee-dot" />
            <span>Your kit</span>
            <span className="ootopia-marquee-dot" />
            <span>An honest recipe</span>
            <span className="ootopia-marquee-dot" />
            <span>Try-on preview</span>
            <span className="ootopia-marquee-dot" />
          </div>
        </div>

        {/* Features */}
        <section className="ootopia-bento" aria-labelledby="bento-title">
          <div className="ootopia-bento-head">
            <p className="ootopia-elite-eyebrow">A workflow that fits you</p>
            <h2 id="bento-title" className="ootopia-reveal-line">
              From inspiration to <em>your own look.</em>
            </h2>
            <p className="ootopia-bento-lede">Five steps, one clear path. No gaps, no guesswork.</p>
          </div>

          <div className="ootopia-bento-grid">
            <article className="ootopia-bento-card ootopia-bento-card--hero group">
              <div className="ootopia-bento-card-media overflow-hidden">
                <img
                  className="ootopia-scale-img group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                  src="/homepage/journey-recipe-match.png"
                  alt="Recipe match showing owned and substituted products"
                  loading="eager"
                  decoding="async"
                />
              </div>
              <div className="ootopia-bento-card-body">
                <h3>Keep the look. Change the recipe.</h3>
                <p>
                  Ootopia maps the visible makeup to products you already own — and flags the one
                  swap that completes it.
                </p>
                <dl className="ootopia-bento-mini">
                  <div>
                    <dt>Owned</dt>
                    <dd>Dewy base from your kit</dd>
                  </div>
                  <div>
                    <dt>Closest</dt>
                    <dd>Soft flush — peach that stays soft</dd>
                  </div>
                  <div>
                    <dt>Missing</dt>
                    <dd>One intentional eye swap</dd>
                  </div>
                </dl>
                <Link to="/looks" className="ootopia-bento-link">
                  Explore public looks <span aria-hidden="true">→</span>
                </Link>
              </div>
            </article>

            <article className="ootopia-bento-card group">
              <div className="ootopia-bento-card-media overflow-hidden">
                <img
                  className="ootopia-scale-img group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                  src="/homepage/journey-skin-scan.png"
                  alt="Skin scan capturing tone and undertone"
                  loading="eager"
                  decoding="async"
                />
              </div>
              <div className="ootopia-bento-card-body">
                <h3>Context, not judgement</h3>
                <p>
                  Your tone, undertone, texture, and comfort — not a score — shape every suggestion.
                </p>
              </div>
            </article>

            <article className="ootopia-bento-card group">
              <div className="ootopia-bento-card-media overflow-hidden">
                <img
                  className="ootopia-scale-img group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                  src="/homepage/kit-inventory-board.png"
                  alt="Kit inventory with matched products"
                  loading="eager"
                  decoding="async"
                />
              </div>
              <div className="ootopia-bento-card-body">
                <h3>Your kit, remixed</h3>
                <p>See what matches, what's close, and what's genuinely missing.</p>
              </div>
            </article>

            <article className="ootopia-bento-card group">
              <div className="ootopia-bento-card-media overflow-hidden">
                <img
                  className="ootopia-scale-img group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                  src="/homepage/public-showcase-collage.png"
                  alt="Showcase collage of published looks"
                  loading="eager"
                  decoding="async"
                />
              </div>
              <div className="ootopia-bento-card-body">
                <h3>Preview before you post</h3>
                <p>Try the adapted look, dial the intensity, decide when it feels right.</p>
                <Link to="/import" className="ootopia-bento-link">
                  Adapt a look <span aria-hidden="true">→</span>
                </Link>
              </div>
            </article>

            <article className="ootopia-bento-card ootopia-bento-card--wide group">
              <div className="ootopia-bento-card-media overflow-hidden">
                <img
                  className="ootopia-scale-img group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                  src="/homepage/remix-engine-board.png"
                  alt="Remix engine board with published looks"
                  loading="eager"
                  decoding="async"
                />
              </div>
              <div className="ootopia-bento-card-body">
                <h3>A board worth keeping</h3>
                <p>
                  Publish the remix or keep exploring. Either way, your original photo stays on your
                  device.
                </p>
              </div>
            </article>
          </div>
        </section>

        {/* Recipe transparency */}
        <section className="ootopia-pin-wrap" aria-labelledby="desire-title">
          <div className="ootopia-pin-left">
            <p className="ootopia-elite-eyebrow">The remix, made transparent</p>
            <h2 id="desire-title" className="ootopia-reveal-line">
              Owned. <em>Substituted.</em> Missing — clearly.
            </h2>
            <p className="ootopia-pin-lede">
              Every board shows its work: what you owned, what we swapped, what you're still
              missing. No hidden scores. No beauty ratings. Just the honest recipe.
            </p>
            <div className="ootopia-pin-actions">
              <Link to="/kit" className="ootopia-elite-btn ootopia-elite-btn--dark">
                Open my kit
              </Link>
              <Link to="/looks" className="ootopia-elite-btn ootopia-elite-btn--ghost">
                Browse looks
              </Link>
            </div>
          </div>
          <div className="ootopia-stack" aria-hidden="false">
            <article className="ootopia-stack-card">
              <span className="ootopia-stack-kicker">01 — Inspiration</span>
              <h3>Rose Glow, as the creator wore it</h3>
              <img
                src="/homepage/journey-creator-tutorial.png"
                alt="Creator look reference with visible makeup steps"
                loading="eager"
                decoding="async"
                className="ootopia-scale-img"
              />
              <p>
                We detect the visible steps — base, flush, soft eye, defined lip — without guessing
                at products.
              </p>
            </article>
            <article className="ootopia-stack-card">
              <span className="ootopia-stack-kicker">02 — Adapted to you</span>
              <h3>The same look, built from your kit</h3>
              <img
                src="/homepage/skin-context-board.png"
                alt="Skin context guiding which products match you"
                loading="eager"
                decoding="async"
                className="ootopia-scale-img"
              />
              <p>
                Dewy base kept. Peach blush swapped from your shelf. One eye product honestly
                flagged as missing.
              </p>
            </article>
            <article className="ootopia-stack-card ootopia-stack-card--accent">
              <span className="ootopia-stack-kicker">03 — Privacy by design</span>
              <h3>Post the remix, delete the source</h3>
              <p>
                Public looks never include skin data or your original photo. Delete your facial data
                anytime — instantly, and auditable.
              </p>
              <Link to="/settings/privacy" className="ootopia-bento-link">
                Privacy controls <span aria-hidden="true">→</span>
              </Link>
            </article>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="ootopia-closing" aria-labelledby="closing-title">
          <p className="ootopia-elite-eyebrow ootopia-closing-eyebrow">
            Start discovering your taste
          </p>
          <h2 id="closing-title" className="ootopia-reveal-line">
            Your canvas. <em>Your way.</em>
          </h2>
          <p className="ootopia-closing-lede">
            A mobile PWA — no app store, no download. Browse public looks right now; create an
            account only when you're ready to save or publish.
          </p>
          <div className="ootopia-elite-actions ootopia-closing-actions">
            <Link
              to="/looks"
              className="ootopia-elite-btn ootopia-elite-btn--dark ootopia-elite-btn--xl"
            >
              Explore public looks
            </Link>
            <Link
              to="/import"
              className="ootopia-elite-btn ootopia-elite-btn--ghost ootopia-elite-btn--xl"
            >
              Start a look
            </Link>
          </div>
        </section>
      </main>

      <footer className="ootopia-elite-footer">
        <div className="ootopia-elite-footer-inner">
          <div className="ootopia-elite-brand ootopia-elite-brand--footer" aria-hidden="true">
            <img src="/brand/ootopia-mark-alpha.png" alt="" className="ootopia-elite-brand-mark" />
            <img
              src="/brand/ootopia-wordmark-alpha.png"
              alt=""
              className="ootopia-elite-brand-wordmark"
            />
          </div>
          <div className="ootopia-elite-footer-links">
            <Link to="/settings/privacy">Privacy</Link>
            <Link to="/looks">Looks</Link>
            <a href="mailto:hello@ootopia.local">Contact</a>
          </div>
        </div>
        <p className="ootopia-elite-footer-note">
          No medical diagnosis. No shade-match guarantees. Honest recommendations only.
        </p>
      </footer>
    </div>
  )
}
